import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input } from '../../../../components/ui/input';
import { useReportStore } from '../../../../store/reportStore';
import { TrashIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../../../axiosConfig';

type ReportItem = {
    id: number;
    title: string;
    pricePerTeam: string;
    minTeams: string;
    maxTeams: string;
    vatPercent: string;
};

const MAX_ITEMS = 20;

const emptyItem = (id: number): ReportItem => ({
    id,
    title: '',
    pricePerTeam: '',
    minTeams: '',
    maxTeams: '',
    vatPercent: '',
});

const ReportCreate: React.FC = () => {
    const [reportTitle, setReportTitle] = useState('');
    const [invoiceDate, setInvoiceDate] = useState('');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [items, setItems] = useState<ReportItem[]>([emptyItem(1)]);
    const [isLoading, setIsLoading] = useState(false);

    const report = useReportStore((state) => state.report);
    const bumpRefresh = useReportStore((state) => state.bumpRefresh);
    const location = useLocation();
    const editId = new URLSearchParams(location.search).get('edit');
    const navigate = useNavigate();

    const dottedToISO = (d: string) => {
        // Convert dd.mm.yyyy -> yyyy-mm-dd
        const m = d?.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (!m) return '';
        const [, dd, mm, yyyy] = m;
        return `${yyyy}-${mm}-${dd}`;
    };

    useEffect(() => {
        if (!report) return;
        setReportTitle(report.title || '');
        setInvoiceDate(dottedToISO(report.invoiceDate || ''));
        setDateStart(dottedToISO(report.datumVon || ''));
        setDateEnd(dottedToISO(report.datumBis || ''));

        const mapped = (report.list || []).slice(0, MAX_ITEMS).map((it, idx) => ({
            id: idx + 1,
            title: it.text ?? '',
            pricePerTeam: String(it.price ?? ''),
            minTeams: String(it.min ?? ''),
            maxTeams: String(it.max ?? ''),
            vatPercent: String(it.vat ?? ''),
        }));
        setItems(mapped.length ? mapped : [emptyItem(1)]);
    }, [report]);

    const canAddMore = items.length < MAX_ITEMS;

    const handleAddItem = () => {
        if (!canAddMore) return;
        setItems((prev) => [...prev, emptyItem(prev.length + 1)]);
    };

    const handleRemoveItem = (id: number) => {
        setItems((prev) => prev.filter((i) => i.id !== id).map((i, idx) => ({ ...i, id: idx + 1 })));
    };

    const updateItem = (id: number, field: keyof ReportItem, value: string | boolean) => {
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, [field]: value } as ReportItem : i)));
    };

    const resetForm = () => {
        setReportTitle('');
        setInvoiceDate('');
        setDateStart('');
        setDateEnd('');
        setItems([emptyItem(1)]);
        navigate('/reports?reset=1');
    };

    const isSubmitDisabled = useMemo(() => {
        if (!reportTitle || !invoiceDate) return true;
        if (!dateStart || !dateEnd) return true;
        return false;
    }, [reportTitle, invoiceDate, dateStart, dateEnd]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setIsLoading(true);

        const payload: any = {
            title: reportTitle.trim(),
            invoiceDate,
            dateStart,
            dateEnd,
            items: items.map((it) => ({
                title: it.title.trim(),
                pricePerTeam: it.pricePerTeam,
                minTeams: it.minTeams,
                maxTeams: it.maxTeams,
                vatPercent: it.vatPercent,
            })),
        };
        const url = editId ? '/association/report/edit' : '/association/report/create';
        if (editId) {
            payload.id = Number(editId);
        }


        axiosInstance.post(url, payload).then((response) => {
            console.log('ReportCreate submit response:', response.data);
            setIsLoading(false);
            if (!editId) {
                resetForm();
            }
            bumpRefresh();
        }).catch((error) => {
            console.log('ReportCreate submit error:', error);
            setIsLoading(false);
        });

    };

    return (
        <div className=" flex-1 bg-white relative border rounded-md p-4">
            <form className="grid gap-4" onSubmit={handleSubmit}>
                <div className="flex h-10 items-center justify-between">
                    <div className="font-medium flex-1 text-lg">Neue Meldung</div>

                    {reportTitle.length > 0 && (
                        <div className="border border-red-200 bg-red-50 items-center py-1 hover:border-red-500 cursor-pointer px-1.5 rounded-md flex items-center gap-1 justify-center" onClick={resetForm}>
                            <TrashIcon className="w-4 h-4 text-red-500" />
                            <span className="text-[11px] text-red-500">Formular zurücksetzen</span>
                        </div>
                    )}
                </div>

                <div className="grid gap-2">
                    <label className="text-sm text-gray-600">Titel</label>
                    <Input className="font-bold !text-[15px] !py-6" value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} placeholder="Titel der Meldung" />
                </div>

                <div className="grid gap-2">
                    <label className="text-sm text-gray-600">Rechnungsdatum</label>
                    <Input type="date" className="font-bold text-sm" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                        <label className="text-sm text-gray-600">Meldedatum ab</label>
                        <Input type="date" className="font-bold text-sm" value={dateStart} onChange={(e) => setDateStart(e.target.value)} placeholder="YYYY-MM-DD" />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-sm text-gray-600">Meldeschluss</label>
                        <Input type="date" className="font-bold text-sm" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} placeholder="YYYY-MM-DD" />
                    </div>
                </div>

                <div className="h-px bg-gray-200 my-2" />

                <div className="flex items-center justify-between">
                    <div className="font-medium">Liste der Meldeoptionen</div>
                    <div className="text-xs text-gray-500">{items.length} / {MAX_ITEMS}</div>
                </div>

                <div className="grid gap-3">
                    {items.map((item) => (
                        <div key={item.id} className="bg-gray-100 rounded-md p-3 grid gap-3">
                            <div className="text-xs text-gray-500">#{item.id}</div>
                            <div className="grid gap-2">
                                <label className="text-sm text-gray-600">Titel</label>
                                <Input value={item.title} onChange={(e) => updateItem(item.id, 'title', e.target.value)} placeholder="Titel" />
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div className="grid gap-2">
                                    <label className="text-sm text-gray-600">Preis (€)</label>
                                    <Input inputMode="decimal" value={item.pricePerTeam} onChange={(e) => updateItem(item.id, 'pricePerTeam', e.target.value.replace(/[^0-9.,]/g, ''))} placeholder="0,00" />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-sm text-gray-600">Min Teams</label>
                                    <Input inputMode="numeric" value={item.minTeams} onChange={(e) => updateItem(item.id, 'minTeams', e.target.value.replace(/[^0-9]/g, ''))} placeholder="1" />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-sm text-gray-600">Max Teams</label>
                                    <Input inputMode="numeric" value={item.maxTeams} onChange={(e) => updateItem(item.id, 'maxTeams', e.target.value.replace(/[^0-9]/g, ''))} placeholder="10" />
                                </div>
                                <div className="grid gap-2">
                                    <label className="text-sm text-gray-600">MwSt. (%)</label>
                                    <Input inputMode="decimal" value={item.vatPercent} onChange={(e) => updateItem(item.id, 'vatPercent', e.target.value.replace(/[^0-9.,]/g, ''))} placeholder="0" />
                                </div>
                            </div>
                            <div className="flex items-center justify-end">
                                <div className="flex items-center font-bold gap-3">
                                    {item.id !== 1 && (
                                        <button type="button" onClick={() => handleRemoveItem(item.id)} className="text-xs text-red-600 hover:underline">
                                            Löschen
                                        </button>
                                    )}
                                    <button type="button" onClick={handleAddItem} disabled={!canAddMore} className={`text-xs ${canAddMore ? 'text-green-700 hover:underline' : 'text-gray-400 cursor-not-allowed'}`}>
                                        Neue Option
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex sticky bottom-0 border-t bg-white p-3 justify-end" style={{ boxShadow: '0 -13px 14px 0 rgba(0, 0, 0, 0.05)' }}>
                    <button type="submit" disabled={isSubmitDisabled || isLoading} className={`px-3 py-2 rounded-md text-sm text-white ${isSubmitDisabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#52A445] hover:bg-green-700'}`}>
                        {isLoading ? 'Speichern ...' : 'Speichern'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default ReportCreate;



